import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { useAuth } from '@context/AuthContext/AuthContext'
import { getUserOrgsOnce, subscribeToUserOrgs } from '@services/OrganizationsService'
import { getOurCauses } from '@services/CausesService'
import { useCauses } from '@context/CausesContext/CausesContext'
import { useOrganizationFeed } from '@hooks/useOrganizationFeed'
import { useOrganizationData } from '@hooks/useOrganizationData'
import { useOrganizationMembers } from '@hooks/useOrganizationMembers'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { Cause } from '@Interfaces/Data/CauseInterface'
import {
  OrganizationContextProps,
  OrganizationContextDefaultValues,
} from '@Interfaces/Data/OrganizationInterface'
import { ContextComponentProps } from '@Interfaces/Util/CommonInterface'

export const OrganizationContext = createContext<OrganizationContextProps>(
  OrganizationContextDefaultValues
)

export const OrganizationProvider = ({ children }: ContextComponentProps) => {
  const { currentUser, onboarding, setOnboarding } = useAuth()
  const { allCauses } = useCauses()

  const [ourOrgs, setOurOrgs] = useState<Org[]>([])
  const [selectedOrg, setSelectedOrg] = useState<Org | null>(null)
  const [causes, setCauses] = useState<Cause[]>([])
  const [coverPhoto, setCoverPhoto] = useState<string | null>(null)
  const [newOrg, setNewOrg] = useState<Org | null>(null)
  const [newOrgCoverPhotos, setNewOrgCoverPhotos] = useState<string[]>([])
  const [pendingChanges, setPendingChanges] = useState(false)
  const [exitModal, setExitModal] = useState(false)

  // Use custom hooks for business logic
  const { onNotAloud } = useOrganizationData(ourOrgs, selectedOrg, setSelectedOrg)
  const { feed, refreshing, onRefresh, loadMoreFeed } = useOrganizationFeed(selectedOrg)
  const {
    members,
    setMembers,
    users,
    setUsers,
    invitedMembers,
    owner,
    userType,
    currentMember,
    sponsors,
    loading,
    stripeConnectAccount,
  } = useOrganizationMembers({
    selectedOrg,
    currentUser,
    ourOrgs,
    setSelectedOrg,
    onNotAloud,
  })

  useEffect(() => {
    if (!currentUser?.id) return

    let isActive = true
    getUserOrgsOnce(currentUser.id)
      .then((resp) => {
        if (!isActive || !resp.success) return
        const orgs = resp.data || []

        if (orgs.length === 0) {
          setOnboarding(true)
          return
        }

        if (!onboarding) setOnboarding(false)
      })
      .catch(() => {})

    return () => {
      isActive = false
    }
  }, [currentUser?.id, onboarding, setOnboarding])

  useEffect(() => {
    if (!currentUser?.id) {
      setOurOrgs([])
      setSelectedOrg(null)
      return
    }

    const unsubscribe = subscribeToUserOrgs(
      currentUser.id,
      (orgs: Org[]) => {
        setOurOrgs(orgs || [])

        setSelectedOrg((current) => {
          if (!orgs || orgs.length === 0) return null
          if (current) {
            const next = orgs.find((x) => x.id === current.id)
            if (next) return next
          }
          return orgs[0]
        })
      },
      (error) => {
        console.error('Error loading organizations:', error)
      }
    )

    return () => unsubscribe()
  }, [currentUser?.id])

  // Refresh feed when org changes
  useEffect(() => {
    if (!selectedOrg) return
    onRefresh(true)
    setCoverPhoto(selectedOrg.coverPhoto || null)
  }, [selectedOrg, onRefresh])

  // Reset pending changes when org changes
  useEffect(() => {
    if (!selectedOrg) return
    setPendingChanges(false)
  }, [selectedOrg?.id])

  // Set our causes
  useEffect(() => {
    if (!selectedOrg || !allCauses || allCauses.length === 0) return
    const causeIds = selectedOrg.causes || []
    const filtered = getOurCauses(causeIds, allCauses)
    setCauses(filtered)
  }, [selectedOrg?.id, allCauses.length])

  // Update cover photo on org
  useEffect(() => {
    if (!coverPhoto) return
    setSelectedOrg((current) => (current ? { ...current, coverPhoto } : current))
  }, [coverPhoto])

  // Memoize org options calculation
  const ourOrgOptions = useMemo(
    () =>
      ourOrgs.map((x) => ({
        value: x.id || '',
        label: x.name || '',
        org: x,
      })),
    [ourOrgs]
  )

  // Sync selected org when ourOrgs changes
  useEffect(() => {
    if (!selectedOrg) return
    const find = ourOrgs.find((x) => x.id === selectedOrg.id)
    if (find && find !== selectedOrg) setSelectedOrg(find)
  }, [ourOrgs, selectedOrg?.id])

  // Reset cover photos when new org is cleared
  useEffect(() => {
    if (!newOrg) {
      setNewOrgCoverPhotos([])
    }
  }, [newOrg])

  const contextValue = useMemo(
    () => ({
      ourOrgs,
      setOurOrgs,
      ourOrgOptions,
      setOurOrgOptions: () => {}, // No-op: ourOrgOptions is now computed from ourOrgs
      selectedOrg,
      setSelectedOrg,
      members,
      setMembers,
      users,
      setUsers,
      invitedMembers,
      currentMember,
      causes,
      setCauses,
      coverPhoto,
      setCoverPhoto,
      owner,
      userType,
      sponsors,
      loading,
      newOrg,
      setNewOrg,
      newOrgCoverPhotos,
      setNewOrgCoverPhotos,
      feed,
      refreshing,
      onRefresh,
      loadMoreFeed,
      stripeConnectAccount,
      pendingChanges,
      setPendingChanges,
      exitModal,
      setExitModal,
    }),
    [
      ourOrgs,
      ourOrgOptions,
      selectedOrg,
      members,
      users,
      invitedMembers,
      currentMember,
      causes,
      coverPhoto,
      owner,
      userType,
      sponsors,
      loading,
      newOrg,
      newOrgCoverPhotos,
      feed,
      refreshing,
      onRefresh,
      loadMoreFeed,
      stripeConnectAccount,
      pendingChanges,
      exitModal,
    ]
  )

  return (
    <OrganizationContext.Provider value={contextValue}>{children}</OrganizationContext.Provider>
  )
}

export const useOrganization = (): OrganizationContextProps => useContext(OrganizationContext)
